import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ScrollText, Save, Maximize2, Check, Info, Clock, Sparkles, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Mapping from product id → max video duration (in seconds) for the script length guideline.
// Source: DEFAULT_PRODUCTS deliverables in /app/backend/server.py
const PRODUCT_DURATION = {
    entry_ai: { sec: 45, label: "max. 25-45s reel" },
    basic_ai: { sec: 80, label: "max. 1m20" },
    standard: { sec: 120, label: "max. 2 min" },
    deluxe: { sec: 300, label: "max. 5 min" },
};

// Author role → color config
export const ROLE_COLORS = {
    admin: { bar: "#E5A93D", text: "text-[#E5A93D]", label: "Social Factory", chipBg: "bg-[#E5A93D]/15", chipBorder: "border-[#E5A93D]/40" },
    marketing_manager: { bar: "#7C3AED", text: "text-[#7C3AED]", label: "Marketing", chipBg: "bg-[#7C3AED]/15", chipBorder: "border-[#7C3AED]/40" },
    agent: { bar: "#10B981", text: "text-emerald-600", label: "Makelaar", chipBg: "bg-emerald-500/15", chipBorder: "border-emerald-500/40" },
};
const UNKNOWN = { bar: "#52525B", text: "text-zinc-500", label: "Onbekend", chipBg: "bg-zinc-200", chipBorder: "border-zinc-300" };

export const roleStyle = (role) => ROLE_COLORS[role] || UNKNOWN;

// Split plain text into paragraphs on empty lines
const splitText = (text) => (text || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

// Available script tones — passed to the AI generator. Order is the dropdown order.
const TONES = [
    { id: "warm", label: "Warm", desc: "Persoonlijk & uitnodigend" },
    { id: "zakelijk", label: "Zakelijk", desc: "Helder & feitelijk" },
    { id: "luxe", label: "Luxe", desc: "Premium & cinematisch" },
    { id: "familie", label: "Familie", desc: "Huiselijk & gericht op gezinnen" },
];

export default function ScriptEditor({ requestId, initialBlocks, initialScript, updatedAt, updatedBy, canOpenFullscreen = true, onChange, items, products }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [editMode, setEditMode] = useState(false);
    const [text, setText] = useState(() => {
        if (initialBlocks && initialBlocks.length) return initialBlocks.map((b) => b.text).join("\n\n");
        return initialScript || "";
    });
    const [blocks, setBlocks] = useState(initialBlocks || []);
    const [saving, setSaving] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    const [generating, setGenerating] = useState(false);

    const generate = async (tone = "warm") => {
        // The dropdown choice itself is the explicit confirmation; no extra window.confirm
        // (Chrome silently blocks repeated confirms which made the button feel broken).
        setGenerating(true);
        try {
            const { data } = await api.post(`/requests/${requestId}/script/generate`, {
                duration_seconds: durationInfo?.sec || 80,
                tone,
            });
            setText(data.script);
            setEditMode(true);
            const toneLabel = TONES.find((t) => t.id === tone)?.label || tone;
            toast.success(`Script gegenereerd (~${data.target_words} woorden, ${toneLabel}). Vergeet niet op te slaan.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI-generatie mislukt");
        }
        setGenerating(false);
    };

    useEffect(() => {
        const bs = initialBlocks && initialBlocks.length ? initialBlocks : (initialScript ? [{ id: null, text: initialScript }] : []);
        setBlocks(bs);
        setText(bs.map((b) => b.text).join("\n\n"));
    }, [initialBlocks, initialScript]);

    const save = async () => {
        const paragraphs = splitText(text);
        // Match each paragraph to existing block by text to preserve author
        const existingByText = new Map();
        blocks.forEach((b) => existingByText.set(b.text, b));
        const payloadBlocks = paragraphs.map((p) => {
            const existing = existingByText.get(p);
            return existing ? { id: existing.id, text: p } : { text: p };
        });
        setSaving(true);
        try {
            const { data } = await api.patch(`/requests/${requestId}/script`, { blocks: payloadBlocks });
            toast.success("Script opgeslagen");
            setBlocks(data.script_blocks || []);
            setText((data.script_blocks || []).map((b) => b.text).join("\n\n"));
            setEditMode(false);
            setJustSaved(true);
            setTimeout(() => setJustSaved(false), 2200);
            onChange && onChange(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Opslaan mislukt"); }
        setSaving(false);
    };

    const authorsSet = useMemo(() => {
        const roles = new Set(blocks.map((b) => b.author_role).filter(Boolean));
        return Array.from(roles);
    }, [blocks]);

    // Determine the max script duration based on the booked products.
    // Pick the product with the longest deliverable duration so the budget reflects the longest video.
    const durationInfo = useMemo(() => {
        if (!items || !items.length) return null;
        let best = null;
        for (const it of items) {
            const d = PRODUCT_DURATION[it.product_id];
            if (d && (!best || d.sec > best.sec)) {
                const p = (products || []).find((x) => x.id === it.product_id);
                best = { ...d, productName: p?.name || it.product_id };
            }
        }
        return best;
    }, [items, products]);

    // Word count + spoken-time estimate (Dutch ≈ 150 wpm = 2.5 words/sec)
    const wordCount = useMemo(() => (text || "").trim().split(/\s+/).filter(Boolean).length, [text]);
    const estimatedSeconds = Math.round(wordCount / 2.5);
    const overBudget = durationInfo ? estimatedSeconds > durationInfo.sec : false;

    const formatSec = (s) => {
        if (s < 60) return `${s}s`;
        const m = Math.floor(s / 60);
        const r = s % 60;
        return r === 0 ? `${m}m` : `${m}m${String(r).padStart(2, "0")}`;
    };

    return (
        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="script-editor">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <ScrollText className="h-4 w-4 text-[#E5A93D]" />
                    <h2 className="font-display font-bold text-xl tracking-tight">Script als leidraad</h2>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {isAdmin && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    disabled={generating}
                                    variant="ghost"
                                    className="h-9 text-xs border border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#7C3AED] hover:bg-[#7C3AED]/20 rounded-sm active:scale-[0.98]"
                                    data-testid="generate-script-btn"
                                >
                                    <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                                    {generating ? "Genereren..." : (text.trim() ? "Regenereer met andere toon" : "AI genereer script")}
                                    <ChevronDown className="h-3 w-3 ml-1.5 opacity-70" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="bg-[#FFFFFF] border-zinc-200 text-zinc-950 w-64"
                                data-testid="script-tone-menu"
                            >
                                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Kies een toon</DropdownMenuLabel>
                                <DropdownMenuSeparator className="bg-zinc-100" />
                                {TONES.map((t) => (
                                    <DropdownMenuItem
                                        key={t.id}
                                        onSelect={() => generate(t.id)}
                                        className="focus:bg-[#7C3AED]/15 focus:text-zinc-950 cursor-pointer py-2"
                                        data-testid={`script-tone-${t.id}`}
                                    >
                                        <div>
                                            <div className="text-sm font-medium text-zinc-950">{t.label}</div>
                                            <div className="text-[11px] text-zinc-500">{t.desc}</div>
                                        </div>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    {!editMode && blocks.length > 0 && (
                        <Button onClick={() => setEditMode(true)} variant="ghost" className="h-9 text-xs border border-zinc-200 text-zinc-600 hover:bg-zinc-100 rounded-sm" data-testid="toggle-edit-script">
                            Bewerken
                        </Button>
                    )}
                    {canOpenFullscreen && blocks.length > 0 && (
                        <Link
                            to={`/projects/${requestId}/script`}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98] transition"
                            data-testid="open-script-fullscreen"
                        >
                            <Maximize2 className="h-3.5 w-3.5" /> Opnamemode
                        </Link>
                    )}
                </div>
            </div>
            <p className="text-xs text-zinc-500 mb-4">
                Zowel Social Factory, de makelaar als de marketing manager kunnen dit script bewerken.
            </p>

            {durationInfo && (
                <div
                    className={`mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 rounded-sm border ${overBudget ? "bg-[#EF4444]/10 border-[#EF4444]/40" : "bg-[#E5A93D]/8 border-[#E5A93D]/30"}`}
                    data-testid="script-duration-hint"
                >
                    <div className="flex items-center gap-2">
                        <Clock className={`h-4 w-4 flex-shrink-0 ${overBudget ? "text-[#EF4444]" : "text-[#E5A93D]"}`} />
                        <div className="text-sm">
                            <span className="text-zinc-600">Maximale scriptduur voor </span>
                            <span className="font-bold text-zinc-950">{durationInfo.productName}</span>
                            <span className="text-zinc-600">: </span>
                            <span className={`font-bold ${overBudget ? "text-[#EF4444]" : "text-[#E5A93D]"}`}>{durationInfo.label}</span>
                        </div>
                    </div>
                    <div className="text-xs text-zinc-500 flex items-center gap-3" data-testid="script-word-counter">
                        <span>{wordCount} {wordCount === 1 ? "woord" : "woorden"}</span>
                        <span className="text-zinc-400">•</span>
                        <span className={overBudget ? "text-[#EF4444] font-bold" : ""}>
                            ~{formatSec(estimatedSeconds)} / {formatSec(durationInfo.sec)}
                        </span>
                    </div>
                </div>
            )}

            {editMode || blocks.length === 0 ? (
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Welkom bij deze prachtige woning...&#10;&#10;Schrijf of plak hier het script. Gebruik lege lijnen tussen paragrafen."
                    rows={10}
                    className="w-full bg-[#FAFAFA] border border-zinc-200 rounded-sm p-4 text-sm text-zinc-800 font-body leading-relaxed resize-y focus:outline-none focus:border-[#E5A93D] focus:ring-1 focus:ring-[#E5A93D]/20"
                    data-testid="script-textarea"
                />
            ) : (
                <div className="bg-[#FAFAFA] rounded-sm border border-zinc-100 p-4 space-y-4" data-testid="script-blocks-view">
                    {blocks.map((b) => (
                        <p key={b.id} className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{b.text}</p>
                    ))}
                </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                <div className="text-xs text-zinc-500">
                    {updatedAt ? (
                        <>Laatst aangepast door <span className="text-zinc-600">{updatedBy}</span> op {new Date(updatedAt).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" })}</>
                    ) : (
                        "Nog geen script opgeslagen"
                    )}
                </div>
                {(editMode || blocks.length === 0) && (
                    <div className="flex items-center gap-2">
                        {editMode && (
                            <Button
                                onClick={() => { setEditMode(false); setText(blocks.map((b) => b.text).join("\n\n")); }}
                                variant="ghost"
                                className="h-9 text-xs border border-zinc-200 text-zinc-600 hover:bg-zinc-100 rounded-sm"
                            >
                                Annuleren
                            </Button>
                        )}
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98] disabled:opacity-50"
                            data-testid="save-script-btn"
                        >
                            {justSaved ? <><Check className="h-4 w-4 mr-1.5" /> Opgeslagen</> : <><Save className="h-4 w-4 mr-1.5" /> {saving ? "Bezig..." : "Script opslaan"}</>}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
